/**
 * React bindings for the analytics taxonomy (ALP-32444).
 */
import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { trace } from "../trace";
import { track } from "./track";
import {
  AnalyticsAction,
  AnalyticsModule,
  moduleFromPath,
  type AnalyticsAttributes,
} from "./taxonomy";

/**
 * The collector only keeps measurement keys that look like `*_duration`,
 * `value_*`, `*_time`, or a known web-vital name — everything else is parsed
 * and then dropped on the floor. Naming this constant makes the constraint
 * visible at the one place it matters; see `portal_events_collector.py`.
 */
const ROUTE_CHANGE_DURATION_KEY = "route_change_duration";
const NAVIGATION_MEASUREMENT_TYPE = "portal-navigation";

/**
 * Was this route change aborted by the user rather than broken?
 *
 * Next.js throws a literal `"Route Cancelled"` when a navigation starts and another is triggered
 * before it resolves — ordinary impatient clicking, not a fault. It is also overwhelmingly the
 * common case: all 10,519 `action.error` events in the dashboard's first fortnight were this one
 * cause, so the error panel showed nothing but noise and a real route failure would have been
 * invisible among them. Exported so the classification is testable on its own; it is the whole
 * difference between an error signal and a wasted one.
 */
export function isCancelledNavigation(error?: { message?: string } | null): boolean {
  return /route cancelled/i.test(error?.message ?? "");
}

/**
 * Emit `page.view` for the initial load and every client-side route change,
 * keep Faro's view name in sync, and measure SPA navigation time.
 *
 * Mount once, in `_app`. Faro's own instrumentation covers the hard navigation
 * only — without this, every in-app route change is invisible, which is most of
 * a session in a portal like this one.
 */
export function useAnalyticsPageViews(): void {
  const router = useRouter();
  // Previous path, so each view can be attributed to where it came from.
  const previousPath = useRef<string | null>(null);
  const navigationStartedAt = useRef<number | null>(null);

  // Initial load. Deliberately separate from the router subscription: the
  // first view fires no routeChange event, and merging the two ends up either
  // double-counting it or missing it entirely.
  useEffect(() => {
    const path = window.location.pathname;
    const module = moduleFromPath(path);

    trace.setView(module);
    track(module, AnalyticsAction.PageView, {
      path,
      navigation_type: "initial",
      referrer: document.referrer || undefined,
    });
    previousPath.current = path;
    // Mount-only: the router subscription below owns everything after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleStart = () => {
      navigationStartedAt.current = Date.now();
    };

    const handleComplete = (url: string) => {
      const path = (url || "").split("?")[0];
      const module = moduleFromPath(path);
      const from = previousPath.current;

      trace.setView(module);
      track(module, AnalyticsAction.PageView, {
        path,
        navigation_type: "spa",
        from_path: from ?? undefined,
        from_module: from ? moduleFromPath(from) : undefined,
      });

      // A module boundary crossing is worth counting on its own — it answers
      // "which modules do people actually use" without post-hoc path parsing.
      if (from && moduleFromPath(from) !== module) {
        track(module, AnalyticsAction.ModuleEnter, { from_module: moduleFromPath(from) });
      }

      if (navigationStartedAt.current !== null) {
        trace.pushMeasurement(NAVIGATION_MEASUREMENT_TYPE, {
          [ROUTE_CHANGE_DURATION_KEY]: Date.now() - navigationStartedAt.current,
        });
        navigationStartedAt.current = null;
      }

      previousPath.current = path;
    };

    const handleError = (error: Error, url: string) => {
      navigationStartedAt.current = null;

      const cancelled = isCancelledNavigation(error);
      track(
        moduleFromPath(url),
        cancelled ? AnalyticsAction.NavCancelled : AnalyticsAction.ActionError,
        {
          target: "route_change",
          result: cancelled ? undefined : "failure",
          error_message: error?.message,
        }
      );
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleError);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleError);
    };
  }, [router.events]);
}

/**
 * Bind a module for the lifetime of a component:
 *
 *   const track = useAnalytics(AnalyticsModule.LclBooking)
 *   track(AnalyticsAction.SearchApply, { count: rows.length })
 */
export function useAnalytics(module: AnalyticsModule) {
  return useCallback(
    (action: AnalyticsAction, attributes?: AnalyticsAttributes) =>
      track(module, action, attributes),
    [module]
  );
}
